import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
// @mui
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
import { useAuthContext } from 'src/auth/hooks';

import Iconify from 'src/components/iconify';
import Label from 'src/components/label';

import { useGetMyTodos } from './hooks/use-career-todos';
import { dayOf, dueLabel, sortOpen } from './todo-text';

// ----------------------------------------------------------------------

// The signed-in user's open Careers to-dos across students, most urgent first:
// overdue, then by due day, then undated ones oldest first. The card shows the
// first few; the rest open in a dialog. Each row is one link to the student's
// Careers tab, where it is ticked off.

const SHOWN = 3;

const initials = (name) =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('') || '?';

function TodoRow({ todo, today, userEmail }) {
  const due = dueLabel(todo, today);
  const student = todo.studentName || `Student ${todo.asn}`;
  const fromSomeoneElse = todo.createdBy?.email && todo.createdBy.email.toLowerCase() !== userEmail?.toLowerCase();
  // The avatar takes the urgency colour, so overdue ones stand out down the list
  const tone = due && due.color !== 'default' ? due.color : 'primary';

  return (
    <ListItemButton
      component={RouterLink}
      href={paths.student.career(todo.asn)}
      sx={{ px: 3, py: 1.5, gap: 2, alignItems: 'center' }}
    >
      <Avatar
        sx={(theme) => ({
          width: 36,
          height: 36,
          fontSize: 13,
          fontWeight: 600,
          color: `${tone}.dark`,
          bgcolor: alpha(theme.palette[tone].main, 0.16),
        })}
      >
        {initials(todo.studentName)}
      </Avatar>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere' }}>
          {todo.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div" noWrap>
          {[student, fromSomeoneElse && `from ${todo.createdBy.name || todo.createdBy.email}`].filter(Boolean).join(' · ')}
        </Typography>
        {todo.notes && (
          <Typography variant="caption" color="text.disabled" component="div" noWrap>
            {todo.notes}
          </Typography>
        )}
        {/* On a phone the due date goes under the text */}
        {due && (
          <Label variant="soft" color={due.color} sx={{ mt: 0.75, display: { xs: 'inline-flex', sm: 'none' } }}>
            {due.text}
          </Label>
        )}
      </Box>

      {due && (
        <Label variant="soft" color={due.color} sx={{ flexShrink: 0, display: { xs: 'none', sm: 'inline-flex' } }}>
          {due.text}
        </Label>
      )}
      <Iconify icon="eva:arrow-ios-forward-fill" width={18} sx={{ color: 'text.disabled', flexShrink: 0 }} />
    </ListItemButton>
  );
}

TodoRow.propTypes = {
  todo: PropTypes.object.isRequired,
  today: PropTypes.string.isRequired,
  userEmail: PropTypes.string,
};

export default function MyTodosCard() {
  const { user } = useAuthContext();
  const { myTodos, myTodosLoading } = useGetMyTodos();
  const [open, setOpen] = useState(false);
  const today = dayOf(new Date());
  const todos = useMemo(() => sortOpen(myTodos), [myTodos]);
  const overdue = todos.filter((t) => dueLabel(t, today)?.color === 'error').length;
  const userEmail = user?.emailaddress || user?.email;

  const counts = (
    <>
      <Label variant="soft">{`${todos.length} open`}</Label>
      {overdue > 0 && (
        <Label variant="soft" color="error">
          {`${overdue} overdue`}
        </Label>
      )}
    </>
  );

  const list = (rows) => (
    <Stack divider={<Divider flexItem sx={{ borderStyle: 'dashed' }} />}>
      {rows.map((todo) => (
        <TodoRow key={todo.id} todo={todo} today={today} userEmail={userEmail} />
      ))}
    </Stack>
  );

  return (
    <Card>
      <CardHeader
        title="My To-dos"
        sx={{ pb: 2 }}
        action={
          todos.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ pt: 0.5, pr: 1 }}>
              {counts}
            </Stack>
          )
        }
      />
      <Divider />

      {!myTodosLoading && !todos.length && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ px: 3, py: 2.5 }}>
          <Iconify icon="solar:checklist-minimalistic-bold-duotone" width={32} sx={{ color: 'text.disabled', flexShrink: 0 }} />
          <Typography variant="body2" color="text.secondary">
            Nothing is assigned to you. To-dos you add on a student&apos;s Careers tab, or that a colleague assigns to
            you, show here.
          </Typography>
        </Stack>
      )}

      {list(todos.slice(0, SHOWN))}

      {todos.length > SHOWN && (
        <>
          <Divider />
          <Box sx={{ px: 2, py: 1 }}>
            <Button
              size="small"
              color="inherit"
              onClick={() => setOpen(true)}
              endIcon={<Iconify icon="eva:arrow-ios-forward-fill" width={16} />}
            >
              {`View all ${todos.length}`}
            </Button>
          </Box>
        </>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm" scroll="paper">
        <DialogTitle sx={{ pr: 7 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <span>My To-dos</span>
            {counts}
          </Stack>
          <IconButton aria-label="Close" onClick={() => setOpen(false)} sx={{ position: 'absolute', right: 12, top: 12 }}>
            <Iconify icon="mingcute:close-line" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {list(todos)}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
